// Who "Reply all" addresses. Pure so it can be unit tested; the composer just
// feeds it the thread and renders the result as chips.
//
// Why thread-wide, not latest-message-only: a plain Reply clears Cc, so the
// client's next email usually arrives with no Cc either. Reading only the
// latest inbound message then shows an empty Cc box even though earlier emails
// in the thread had CCs.
//
// Outbound messages count too, but only ones addressed to the client (like
// Gmail's Reply all on a message you sent). A ticket opened by our own outbound
// email has no inbound message at all, and the CCs on it are the whole answer.
// Outbound messages that don't go to the client — Forward targets — are
// skipped: CC'ing a third party onto a client thread is worse than missing a CC.
// Bcc is never read.

// Addresses that are ours but may not have a gmail_inboxes row (aliases and
// the POD relay). Inbox rows are merged in by `buildOwnAddresses`.
const BASE_OWN_ADDRESSES = [
  'support@hbcgroup.com.au',
  'support@practiceacceleration.com',
  'support@mail.practiceacceleration.com',
  'pod@practiceacceleration.com',
];

export interface RecipientMessage {
  direction: string;
  from_email: string | null;
  to_emails?: string[] | null;
  cc_emails?: string[] | null;
}

export interface OwnAddressInbox {
  email_address: string | null;
  send_as_address?: string | null;
}

export interface ReplyAllRecipients {
  to: string[];
  cc: string[];
}

function norm(v: string | null | undefined): string {
  return (v ?? '').toLowerCase().trim();
}

/** Lowercase, trim, drop empties/duplicates and anything in `exclude`. */
export function dedupeAddresses(
  arr: (string | null | undefined)[],
  exclude: ReadonlySet<string> = new Set(),
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of arr) {
    const s = norm(v);
    if (!s || exclude.has(s) || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

export function buildOwnAddresses(
  inboxes: readonly OwnAddressInbox[] | null | undefined,
): Set<string> {
  const out = new Set<string>(BASE_OWN_ADDRESSES);
  for (const ibx of inboxes ?? []) {
    const a = norm(ibx.email_address);
    const s = norm(ibx.send_as_address);
    if (a) out.add(a);
    if (s) out.add(s);
  }
  return out;
}

/** `messages` must be in thread order (oldest first), as `useTicketMessages`
 *  returns them. */
export function deriveReplyAllRecipients(args: {
  messages: readonly RecipientMessage[];
  ticketSenderEmail: string | null | undefined;
  ownAddresses: ReadonlySet<string>;
}): ReplyAllRecipients {
  const { messages, ticketSenderEmail, ownAddresses } = args;
  const inbound = messages.filter((m) => m.direction === 'inbound').reverse(); // newest first

  const latestFrom = norm(inbound[0]?.from_email);
  const sender = norm(ticketSenderEmail);
  const toAddr =
    latestFrom && !ownAddresses.has(latestFrom)
      ? latestFrom
      : sender && !ownAddresses.has(sender)
        ? sender
        : '';
  const to = toAddr ? [toAddr] : [];

  // Who counts as "the client": the ticket sender and everyone who has written
  // to us. An outbound message is a reply to them only if it was sent to one.
  const clientAddrs = new Set<string>(
    [sender, ...inbound.map((m) => norm(m.from_email))].filter((a) => a && !ownAddresses.has(a)),
  );
  const sentToClient = (m: RecipientMessage) =>
    m.direction === 'outbound' && (m.to_emails ?? []).some((a) => clientAddrs.has(norm(a)));

  const exclude = new Set<string>([...ownAddresses, ...to]);
  const cc = dedupeAddresses(
    [
      // The ticket's original sender may differ from the latest author.
      sender,
      // Newest first across inbound + client-addressed outbound.
      ...[...messages].reverse().flatMap((m) =>
        m.direction === 'inbound'
          ? [m.from_email, ...(m.to_emails ?? []), ...(m.cc_emails ?? [])]
          : sentToClient(m)
            ? [...(m.to_emails ?? []), ...(m.cc_emails ?? [])]
            : [],
      ),
    ],
    exclude,
  );
  return { to, cc };
}
